import cn from "classnames";
import {
    documentMasks,
    documentPatterns,
    documentValueFormat,
    validateLicenseExpirationDate,
} from "components/drivers/show/formConfig";
import { Datetime, Field, Select } from "components/shared/form";
import VehicleOwnership from "components/shared/vehicleOwnership";
import { documentConfig, vehicleOwnershipConfig } from "components/shared/vehicleOwnership/config";
import { formatDate } from "helpers/date";
import { countrys, countrysEnum } from "helpers/enums";
import { composeValidators, phoneNumber, requiredField } from "helpers/validators";
import { prepareVehicleOwnershipData, requiresOwnershipBasisDocuments } from "helpers/vehicles/ownership";
import moment from "moment";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { applyInfoToGenerateResponseTitle } from "sources/shippingOrders";

const compactField = (config) => ({
    ...config,
    bWrapper: "col-md-6 col-xl-4 form-group mb-2",
    fieldWrapper: "w-100",
    labelWrapper: "d-block small text-muted mb-1 text-left",
    wrapper: "w-100",
});

const requiredVehicleValue = (config) => ({
    ...config,
    required: true,
    validators: [requiredField, ...(config.validators || [])],
});

const titleDocumentConfig = {
    ...documentConfig,
    additionalInfo: {
        ...documentConfig.additionalInfo,
        inputProps: { ...documentConfig.additionalInfo.inputProps, maxLength: 2000 },
    },
};

const driverConfig = {
    lastName: {
        label: "Фамилия",
        inputProps: { name: "lastName" },
        required: true,
        validators: [requiredField],
    },
    firstName: {
        label: "Имя",
        inputProps: { name: "firstName" },
        required: true,
        validators: [requiredField],
    },
    patronymic: {
        label: "Отчество",
        inputProps: { name: "patronymic" },
    },
    phoneNumber: {
        label: "Телефон",
        type: "phone",
        inputProps: { name: "phoneNumber" },
        required: true,
        validators: [requiredField, phoneNumber],
    },
    inn: {
        label: "ИННФЛ",
        inputProps: { name: "inn", inputMode: "numeric", maxLength: 12 },
        required: true,
        validators: [requiredField],
    },
};

const getLicenseConfig = (issuingCountry, dateOfIssue) => {
    const isBelarus = issuingCountry === countrysEnum.Belarus;
    const countryMasks = documentMasks[issuingCountry]?.license;
    const countryPatterns = documentPatterns[issuingCountry]?.license;
    const minDate = dateOfIssue && moment(dateOfIssue).isValid() ? moment(dateOfIssue).toDate() : undefined;

    return {
        issuingCountry: {
            inputProps: { name: "issuingCountry" },
            label: "Страна выдачи",
            options: countrys,
            placeholder: "Выберите страну",
            required: true,
            validators: [requiredField],
        },
        series: {
            inputProps: {
                name: "series",
                ...(countryMasks ? {} : { maxLength: countryPatterns ? 2 : isBelarus ? 3 : 4 }),
            },
            label: "Серия",
            required: true,
            validators: [
                requiredField,
                countryPatterns
                    ? documentValueFormat(countryPatterns.series, "Некорректная серия водительского удостоверения")
                    : documentValueFormat(
                          isBelarus ? /^\d[A-Za-zА-Яа-яЁёІі]{2}$/ : /^\d{4}$/,
                          "Некорректная серия водительского удостоверения",
                      ),
            ],
            ...(countryMasks ? { type: "customMask", mask: countryMasks.series, maskPlaceholder: "_" } : {}),
        },
        number: {
            inputProps: {
                name: "number",
                ...(countryMasks ? {} : { maxLength: countryPatterns ? 6 : isBelarus ? 7 : 6 }),
            },
            label: "Номер",
            required: true,
            validators: [
                requiredField,
                countryPatterns
                    ? documentValueFormat(countryPatterns.number, "Некорректный номер водительского удостоверения")
                    : documentValueFormat(
                          isBelarus ? /^\d{6,7}$/ : /^\d{6}$/,
                          "Некорректный номер водительского удостоверения",
                      ),
            ],
            ...(countryMasks ? { type: "customMask", mask: countryMasks.number, maskPlaceholder: "_" } : {}),
        },
        dateOfIssue: {
            inputProps: { name: "dateOfIssue", type: "date" },
            label: "Дата выдачи",
            inPortal: true,
            required: true,
            validators: [requiredField],
        },
        expirationDate: {
            inputProps: { name: "expirationDate", type: "date" },
            label: "Дата истечения срока действия",
            inPortal: true,
            validators: [
                (value, data) => validateLicenseExpirationDate(value, { dateOfIssue, ...data }),
            ],
            libOptions: {
                isClearable: true,
                ...(minDate ? { minDate } : {}),
            },
        },
    };
};

const vehicleNumberConfig = (label) => ({
    label,
    inputProps: { name: "number" },
    required: true,
    validators: [requiredField],
});

const carConfig = {
    number: vehicleNumberConfig("Госномер автомобиля"),
    brandTypeId: {
        label: "Марка",
        inputProps: { name: "brandTypeId" },
        dictionary: "carBrandType",
        menuPortalTarget: document.body,
        required: true,
        validators: [requiredField],
    },
    typeId: {
        label: "Тип автомобиля",
        inputProps: { name: "typeId" },
        dictionary: "carType",
        menuPortalTarget: document.body,
        required: true,
        validators: [requiredField],
    },
    carrying: requiredVehicleValue(vehicleOwnershipConfig.carrying),
    volume: requiredVehicleValue(vehicleOwnershipConfig.volume),
    ownershipType: requiredVehicleValue(vehicleOwnershipConfig.ownershipType),
    ownershipBasisDocuments: { value: [], config: titleDocumentConfig },
};

const trailerConfig = {
    number: vehicleNumberConfig("Госномер прицепа"),
    brandTypeId: {
        label: "Марка",
        inputProps: { name: "brandTypeId" },
        dictionary: "trailerBrandType",
        menuPortalTarget: document.body,
    },
    typeId: {
        label: "Тип прицепа",
        inputProps: { name: "typeId" },
        dictionary: "trailerType",
        menuPortalTarget: document.body,
    },
    carrying: requiredVehicleValue(vehicleOwnershipConfig.carrying),
    volume: requiredVehicleValue(vehicleOwnershipConfig.volume),
    ownershipType: requiredVehicleValue(vehicleOwnershipConfig.ownershipType),
    ownershipBasisDocuments: { value: [], config: titleDocumentConfig },
};

const propertyName = (name) => `${name.charAt(0).toUpperCase()}${name.slice(1)}`;

const backendFieldError = (data, name) => {
    const key = propertyName(name);
    const propertyError = Object.entries(data?.propertyErrorMessages || {}).find(
        ([property]) => property.toLowerCase() === key.toLowerCase(),
    )?.[1];
    if (propertyError) {
        return propertyError;
    }

    return (data?.unfilledRequiredProperties || []).some((property) => property.toLowerCase() === key.toLowerCase())
        ? "Обязательное поле"
        : "";
};

const hasValidationIssues = (value) => {
    if (!value || typeof value !== "object") {
        return false;
    }
    if (
        value.hasUnfilledRequiredProperties ||
        value.errorMessage ||
        value.errorMessages?.length ||
        Object.keys(value.propertyErrorMessages || {}).length
    ) {
        return true;
    }

    return Object.values(value).some((item) =>
        Array.isArray(item) ? item.some(hasValidationIssues) : hasValidationIssues(item),
    );
};

const hasFieldIssue = (data, name) => Boolean(backendFieldError(data, name));

const findValidationDocument = (validationDocuments, document, index) =>
    (document.id && validationDocuments.find((item) => item.id === document.id)) || validationDocuments[index];

const hasDocumentFieldIssue = (document, name) => {
    if (!document) {
        return true;
    }
    if (name === "legalPersonId") {
        return hasValidationIssues(document.organization) || hasValidationIssues(document.legalPerson);
    }
    if (name === "counterpartyId") {
        return hasFieldIssue(document, name) || hasValidationIssues(document.counterparty);
    }

    return hasFieldIssue(document, name);
};

const getVehicleVisibility = (validationData, currentData) => {
    const validationDocuments = validationData?.ownershipBasisDocuments || [];
    const ownershipType = hasFieldIssue(validationData, "ownershipType");
    const ownershipBasisDocuments =
        hasFieldIssue(validationData, "ownershipBasisDocuments") ||
        validationDocuments.some(hasValidationIssues) ||
        (ownershipType && requiresOwnershipBasisDocuments(currentData.ownershipType));
    const visibility = {
        number: hasFieldIssue(validationData, "number"),
        brandTypeId: hasFieldIssue(validationData, "brandTypeId"),
        typeId: hasFieldIssue(validationData, "typeId"),
        carrying: hasFieldIssue(validationData, "carrying"),
        volume: hasFieldIssue(validationData, "volume"),
        ownershipType,
        ownershipBasisDocuments,
    };

    return {
        ...visibility,
        hasEditableFields: Object.values(visibility).some(Boolean),
        shouldRenderDocument: (document, index) => {
            const validationDocument = findValidationDocument(validationDocuments, document, index);
            return !validationDocument || hasValidationIssues(validationDocument);
        },
        shouldRenderDocumentField: (document, index, name) =>
            hasDocumentFieldIssue(findValidationDocument(validationDocuments, document, index), name),
    };
};

const clearBackendFieldError = (data, name) => {
    const key = propertyName(name).toLowerCase();
    return {
        ...data,
        unfilledRequiredProperties: (data.unfilledRequiredProperties || []).filter(
            (property) => property.toLowerCase() !== key,
        ),
        propertyErrorMessages: Object.fromEntries(
            Object.entries(data.propertyErrorMessages || {}).filter(([property]) => property.toLowerCase() !== key),
        ),
    };
};

const editableDocument = (document = {}) => ({
    ...document,
    id: document.id || 0,
    name: document.name || "",
    number: document.number || "",
    date: document.date || "",
    externalId: document.externalId || "",
    additionalInfo: document.additionalInfo || "",
    legalPersonId: document.legalPerson?.id || null,
    counterpartyId: document.counterparty?.id || "",
});

const editableVehicle = (vehicle) => ({
    ...vehicle,
    number: vehicle?.number || "",
    typeId: vehicle?.typeId || null,
    brandTypeId: vehicle?.brandTypeId || null,
    carrying: vehicle?.carrying ?? "",
    volume: vehicle?.volume ?? "",
    ownershipType: vehicle?.ownershipType || null,
    ownershipBasisDocuments: (vehicle?.ownershipBasisDocuments || []).map(editableDocument),
});

const editableDriver = (driver) => ({
    ...driver,
    firstName: driver?.firstName || "",
    lastName: driver?.lastName || "",
    patronymic: driver?.patronymic || "",
    phoneNumber: driver?.phoneNumber || "",
    inn: driver?.inn || "",
    driverLicense: {
        ...driver?.driverLicense,
        issuingCountry: driver?.driverLicense?.issuingCountry || null,
        series: driver?.driverLicense?.series || "",
        number: driver?.driverLicense?.number || "",
        dateOfIssue: driver?.driverLicense?.dateOfIssue || "",
        expirationDate: driver?.driverLicense?.expirationDate || "",
    },
});

const requestDocument = (document) => ({
    id: document.id || 0,
    name: document.name || "",
    number: document.number || "",
    date: document.date ? formatDate(document.date) : null,
    externalId: document.externalId || null,
    additionalInfo: document.additionalInfo || null,
    legalPersonId: document.legalPersonId || null,
    counterpartyId: document.counterpartyId,
});

const requestVehicle = (vehicle) => {
    const prepared = prepareVehicleOwnershipData(vehicle);
    return {
        id: prepared.id,
        number: prepared.number || "",
        typeId: prepared.typeId || null,
        brandTypeId: prepared.brandTypeId || null,
        carrying: prepared.carrying === "" ? null : prepared.carrying,
        volume: prepared.volume === "" ? null : prepared.volume,
        ownershipType: prepared.ownershipType || null,
        ownershipBasisDocuments: (prepared.ownershipBasisDocuments || []).map(requestDocument),
    };
};

const SectionIssues = ({ data }) => {
    const errors = [...new Set(data?.errorMessages || [])];
    if (!errors.length) {
        return null;
    }

    return (
        <div className="alert alert-danger py-2 mb-3">
            {errors.map((error) => (
                <div key={error}>{error}</div>
            ))}
        </div>
    );
};

const SectionTitle = ({ children, data }) => (
    <div className="d-flex align-items-center justify-content-between mb-3">
        <h5 className="mb-0">{children}</h5>
        {data?.hasUnfilledRequiredProperties && <span className="badge badge-warning">Нужно заполнить</span>}
    </div>
);

const getRequiredInfoView = (data, driver, car, trailer) => {
    const driverFields = Object.values(driverConfig).filter((config) =>
        hasFieldIssue(data.driver, config.inputProps.name),
    );
    const showAllLicenseFields = hasFieldIssue(data.driver, "driverLicense");
    const hasDateOfIssueIssue = hasFieldIssue(data.driver?.driverLicense, "dateOfIssue");
    const licenseConfig = getLicenseConfig(driver.driverLicense.issuingCountry, driver.driverLicense.dateOfIssue);
    const licenseFields = Object.values(licenseConfig).filter(
        (config) =>
            showAllLicenseFields ||
            hasFieldIssue(data.driver?.driverLicense, config.inputProps.name) ||
            (config.inputProps.name === "expirationDate" && hasDateOfIssueIssue),
    );
    const carVisibility = getVehicleVisibility(data.car, car);
    const trailerVisibility = trailer && getVehicleVisibility(data.trailer, trailer);

    return {
        driverFields,
        licenseFields,
        carVisibility,
        trailerVisibility,
        showDriver: hasValidationIssues(data.driver),
        showLicense: showAllLicenseFields || hasValidationIssues(data.driver?.driverLicense),
        showCar: hasValidationIssues(data.car),
        showTrailer: Boolean(trailer && hasValidationIssues(data.trailer)),
        hasEditableFields:
            driverFields.length > 0 ||
            licenseFields.length > 0 ||
            carVisibility.hasEditableFields ||
            Boolean(trailerVisibility?.hasEditableFields),
    };
};

const isFieldValid = (config, value, data) => {
    if (!config?.validators) {
        return true;
    }
    return !composeValidators(config.validators, value, data);
};

const areVehicleDocumentsValid = (vehicle, visibility) => {
    if (!requiresOwnershipBasisDocuments(vehicle.ownershipType)) {
        return true;
    }
    if (!vehicle.ownershipBasisDocuments || vehicle.ownershipBasisDocuments.length === 0) {
        return false;
    }
    return vehicle.ownershipBasisDocuments.every((doc, index) => {
        if (visibility?.shouldRenderDocument && !visibility.shouldRenderDocument(doc, index)) {
            return true;
        }
        return ["name", "number", "counterpartyId"].every((key) => {
            if (visibility?.shouldRenderDocumentField && !visibility.shouldRenderDocumentField(doc, index, key)) {
                return true;
            }
            return !titleDocumentConfig[key]?.validators || isFieldValid(titleDocumentConfig[key], doc[key], doc);
        });
    });
};

const isVehicleValid = (config, vehicle, visibility) => {
    if (!vehicle || !visibility) {
        return true;
    }
    const fields = ["number", "brandTypeId", "typeId", "carrying", "volume", "ownershipType"];
    const areFieldsValid = fields.every((key) => !visibility[key] || isFieldValid(config[key], vehicle[key], vehicle));
    const areDocumentsValid = !visibility.ownershipBasisDocuments || areVehicleDocumentsValid(vehicle, visibility);

    return areFieldsValid && areDocumentsValid;
};

export const isRequiredInfoFormValid = (driver, car, trailer, view) => {
    const isDriverValid =
        !view.showDriver ||
        view.driverFields.every((cfg) => isFieldValid(cfg, driver[cfg.inputProps.name], driver));

    const isLicenseValid =
        !view.showDriver ||
        !view.showLicense ||
        view.licenseFields.every((cfg) =>
            isFieldValid(cfg, driver.driverLicense?.[cfg.inputProps.name], driver.driverLicense),
        );

    const isCarValid = !view.showCar || isVehicleValid(carConfig, car, view.carVisibility);
    const isTrailerValid = !view.showTrailer || isVehicleValid(trailerConfig, trailer, view.trailerVisibility);

    return isDriverValid && isLicenseValid && isCarValid && isTrailerValid;
};

const DriverSection = ({
    visible,
    driver,
    driverFields,
    licenseFields,
    showLicense,
    renderField,
    renderSelect,
    renderDate,
    setDriverValue,
    setLicenseValue,
}) => {
    if (!visible) {
        return null;
    }

    const renderLicenseField = (config) => {
        if (config.inputProps.type === "date") {
            return renderDate(config, driver.driverLicense, setLicenseValue);
        }
        if (config.options) {
            return renderSelect(config, driver.driverLicense, setLicenseValue);
        }
        return renderField(config, driver.driverLicense, setLicenseValue);
    };

    return (
        <section className="bg-f9-33 border rounded p-3 mb-3">
            <SectionTitle data={driver}>Водитель</SectionTitle>
            <SectionIssues data={driver} />
            <div className="row">{driverFields.map((config) => renderField(config, driver, setDriverValue))}</div>

            {showLicense && (
                <div className="bg-ff-2c border-left border-primary px-3 pt-3 pb-1 mt-2">
                    <SectionTitle data={driver.driverLicense}>Водительское удостоверение</SectionTitle>
                    <SectionIssues data={driver.driverLicense} />
                    <div className="row">{licenseFields.map(renderLicenseField)}</div>
                </div>
            )}
        </section>
    );
};

const VehicleSection = ({ visible, title, data, config, visibility, onChange, renderField, renderSelect }) => {
    if (!visible) {
        return null;
    }

    return (
        <section className="bg-f9-33 border rounded p-3 mb-3">
            <SectionTitle data={data}>{title}</SectionTitle>
            <SectionIssues data={data} />
            <div className="row">
                {visibility.number && renderField(config.number, data, onChange)}
                {visibility.brandTypeId && renderSelect(config.brandTypeId, data, onChange)}
                {visibility.typeId && renderSelect(config.typeId, data, onChange)}
                <VehicleOwnership
                    carrying={{ ...config.carrying, error: backendFieldError(data, "carrying") }}
                    data={data}
                    ownershipBasisDocuments={config.ownershipBasisDocuments}
                    ownershipBasisDocumentsVisible={visibility.ownershipBasisDocuments}
                    ownershipSectionClassName="col-12 bg-ff-2c border-left border-primary pl-3 pr-2 py-2 mt-2 mb-2"
                    ownershipType={{ ...config.ownershipType, error: backendFieldError(data, "ownershipType") }}
                    renderField={(fieldConfig) => renderField(fieldConfig, data, onChange)}
                    renderSelect={(fieldConfig) => renderSelect(fieldConfig, data, onChange)}
                    setValue={onChange}
                    shouldRenderDocument={visibility.shouldRenderDocument}
                    shouldRenderDocumentField={visibility.shouldRenderDocumentField}
                    shouldRenderField={(name) => visibility[name]}
                    volume={{ ...config.volume, error: backendFieldError(data, "volume") }}
                />
            </div>
        </section>
    );
};

const ModalActions = ({ hasEditableFields, loading, back }) => (
    <div className="d-flex justify-content-end mt-3">
        <button className="btn btn-default btn-sm mr-2" disabled={loading} onClick={back} type="button">
            {hasEditableFields ? "Отмена" : "Закрыть"}
        </button>
        {hasEditableFields && (
            <button className="btn btn-brand btn-sm" disabled={loading} type="submit">
                Сохранить и продолжить
            </button>
        )}
    </div>
);

export default function ShippingOrderRequiredInfoModal({ data, bidId, onSuccess, back, enums: propsEnums }) {
    const storeEnums = useSelector((state) => state.dictionaries?.enums);
    const enums = propsEnums || storeEnums || {};
    const [driver, setDriver] = useState(() => editableDriver(data.driver));
    const [car, setCar] = useState(() => editableVehicle(data.car));
    const [trailer, setTrailer] = useState(() => (data.trailer ? editableVehicle(data.trailer) : null));
    const [loading, setLoading] = useState(false);
    const [invalid, setInvalid] = useState(false);
    const view = getRequiredInfoView(data, driver, car, trailer);

    const setDriverValue = ({ name, value }) => {
        setDriver((current) => clearBackendFieldError({ ...current, [name]: value }, name));
    };

    const setLicenseValue = ({ name, value }) => {
        const formattedValue =
            (name === "series" || name === "number") && typeof value === "string" ? value.toUpperCase() : value;
        setDriver((current) => ({
            ...current,
            driverLicense: clearBackendFieldError({ ...current.driverLicense, [name]: formattedValue }, name),
        }));
    };

    const setCarValue = ({ name, value }) => {
        setCar((current) => clearBackendFieldError({ ...current, [name]: value }, name));
    };

    const setTrailerValue = ({ name, value }) => {
        setTrailer((current) => clearBackendFieldError({ ...current, [name]: value }, name));
    };

    const renderField = (config, currentData, onChange) => (
        <Field
            {...compactField(config)}
            error={backendFieldError(currentData, config.inputProps.name)}
            key={config.inputProps.name}
            onChange={onChange}
            value={currentData[config.inputProps.name]}
        />
    );

    const renderSelect = (config, currentData, onChange) => {
        const options = config.options || (config.dictionary ? enums[config.dictionary] : []) || [];
        return (
            <Select
                {...compactField(config)}
                error={backendFieldError(currentData, config.inputProps.name)}
                key={config.inputProps.name}
                onChange={onChange}
                options={options}
                value={currentData[config.inputProps.name]}
            />
        );
    };

    const renderDate = (config, currentData, onChange) => (
        <Datetime
            {...compactField(config)}
            error={backendFieldError(currentData, config.inputProps.name)}
            key={config.inputProps.name}
            onChange={onChange}
            value={currentData[config.inputProps.name]}
        />
    );

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (loading) {
            return;
        }
        if (!isRequiredInfoFormValid(driver, car, trailer, view)) {
            setInvalid(true);
            return;
        }

        setLoading(true);
        setInvalid(false);
        const driverLicense = driver.driverLicense;
        try {
            const result = await applyInfoToGenerateResponseTitle({
                id: bidId,
                driver: {
                    id: driver.id,
                    firstName: driver.firstName,
                    lastName: driver.lastName,
                    patronymic: driver.patronymic || null,
                    phoneNumber: driver.phoneNumber,
                    inn: driver.inn,
                    driverLicense: {
                        issuingCountry: driverLicense.issuingCountry,
                        series: driverLicense.series,
                        number: driverLicense.number,
                        dateOfIssue: formatDate(driverLicense.dateOfIssue),
                        expirationDate: formatDate(driverLicense.expirationDate) || null,
                    },
                },
                car: requestVehicle(car),
                trailer: trailer ? requestVehicle(trailer) : null,
            });

            if (result) {
                back();
                onSuccess?.();
            } else {
                setInvalid(true);
            }
        } catch (_error) {
            setInvalid(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <form className={cn({ loading, "modal-window__form--invalid": invalid })} onSubmit={handleSubmit}>
            <div className="modal-window__body text-left">
                <div className="alert alert-warning mb-3">
                    {view.hasEditableFields
                        ? "Перед формированием ответного титула заполните обязательные данные заявки. После сохранения откроется окно формирования и подписания T2."
                        : "Перед формированием ответного титула исправьте указанные данные в карточке заявки, затем повторите формирование T2."}
                </div>
                {data.errorMessage && <div className="alert alert-danger mb-3">{data.errorMessage}</div>}

                <DriverSection
                    driver={driver}
                    driverFields={view.driverFields}
                    licenseFields={view.licenseFields}
                    renderDate={renderDate}
                    renderField={renderField}
                    renderSelect={renderSelect}
                    setDriverValue={setDriverValue}
                    setLicenseValue={setLicenseValue}
                    showLicense={view.showLicense}
                    visible={view.showDriver}
                />
                <VehicleSection
                    config={carConfig}
                    data={car}
                    onChange={setCarValue}
                    renderField={renderField}
                    renderSelect={renderSelect}
                    title="Тягач"
                    visibility={view.carVisibility}
                    visible={view.showCar}
                />
                <VehicleSection
                    config={trailerConfig}
                    data={trailer}
                    onChange={setTrailerValue}
                    renderField={renderField}
                    renderSelect={renderSelect}
                    title="Прицеп"
                    visibility={view.trailerVisibility}
                    visible={view.showTrailer}
                />
                <ModalActions back={back} hasEditableFields={view.hasEditableFields} loading={loading} />
            </div>
        </form>
    );
}
